import type { FuelLogEnriched } from "@/lib/fuel-analytics";
import { carTitle, formatCurrency, formatDateTime, formatFuelPrice, formatNumber, userDisplayName } from "@/lib/utils";

export function FuelLogsTable({ logs }: { logs: FuelLogEnriched[] }) {
  if (logs.length === 0) {
    return (
      <p className="rounded-2xl bg-canvas p-6 text-center text-sm text-muted">
        No fuel logs match your filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl bg-canvas">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border/40 text-[11px] font-bold uppercase tracking-wider text-muted">
            <th className="px-4 py-3">When</th>
            <th className="px-4 py-3">Vehicle</th>
            <th className="px-4 py-3">Station</th>
            <th className="px-4 py-3">Grade</th>
            <th className="px-4 py-3">Litres</th>
            <th className="px-4 py-3">Price/L</th>
            <th className="px-4 py-3">Total</th>
            <th className="px-4 py-3">Mode</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-b border-border/20 last:border-0">
              <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(log.loggedAt)}</td>
              <td className="px-4 py-3">{log.car ? carTitle(log.car) : log.carID.slice(0, 8)}</td>
              <td className="px-4 py-3">{log.station?.name ?? log.gasStationID.slice(0, 8)}</td>
              <td className="px-4 py-3">{log.fuelGrade}</td>
              <td className="px-4 py-3">{formatNumber(log.litres)} L</td>
              <td className="px-4 py-3">{formatFuelPrice(log.pricePerLitre)}</td>
              <td className="px-4 py-3 font-semibold">{formatCurrency(log.totalCost)}</td>
              <td className="px-4 py-3 capitalize text-muted">{log.inputMode}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function FuelLogsTableWithUser({ logs }: { logs: FuelLogEnriched[] }) {
  if (logs.length === 0) {
    return (
      <p className="rounded-2xl bg-canvas p-6 text-center text-sm text-muted">
        No fuel logs match your filters.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-2xl bg-canvas">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-border/40 text-[11px] font-bold uppercase tracking-wider text-muted">
            <th className="px-4 py-3">When</th>
            <th className="px-4 py-3">User</th>
            <th className="px-4 py-3">Vehicle</th>
            <th className="px-4 py-3">Station</th>
            <th className="px-4 py-3">Grade</th>
            <th className="px-4 py-3">Litres</th>
            <th className="px-4 py-3">Total</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id} className="border-b border-border/20 last:border-0">
              <td className="px-4 py-3 whitespace-nowrap">{formatDateTime(log.loggedAt)}</td>
              <td className="px-4 py-3">{log.user ? userDisplayName(log.user) : log.userID.slice(0, 8)}</td>
              <td className="px-4 py-3">{log.car ? carTitle(log.car) : log.carID.slice(0, 8)}</td>
              <td className="px-4 py-3">{log.station?.name ?? "—"}</td>
              <td className="px-4 py-3">{log.fuelGrade}</td>
              <td className="px-4 py-3">{formatNumber(log.litres)} L</td>
              <td className="px-4 py-3 font-semibold">{formatCurrency(log.totalCost)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
